import * as React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import TitledContainer from '../../components/TitledContainer';
import PlanComparison from './PlanComparison';
import UsageSliders from './UsageSliders';
import FeatureAddons from './FeatureAddons';
import PriceCalculator from './PriceCalculator';
import SubscriptionOverview from './SubscriptionOverview';
import { SubscriptionContainer } from './styled';
import { getAdminStatsAsync, getSubscriptionsAsync, upgradeSubscriptionAsync } from '../../ajax/requests';
import { AdminStats, SubscriptionDataResponse, SubscriptionAddonTier, SubscriptionFeatureDefinition, SubscriptionPlanDefinition, SubscriptionAddonDefinition, SubscriptionData } from '../../models';
import { ToggleButton, ToggleButtonGroup, Button, Tooltip } from '@mui/material';
import { alertSuccess, alertError } from '../../store/alerts/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useGeneralSettings } from '../../store/settings/hooks';
import { getAddonTierPrice } from './utils';
import { isPaymentExpired } from '../../store/session/selectors';

const MySubscription: React.FC = () => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useGeneralSettings();
    const isFrenchCountry = (generalSettings?.countryCode ?? '').toLowerCase().startsWith('fr');
    const paymentExpired = useSelector(isPaymentExpired);

    const [subscriptionData, setSubscriptionData] = useState<SubscriptionDataResponse | null>(null);
    const [adminStats, setAdminStats] = useState<AdminStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [isUpgrading, setIsUpgrading] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

    // Selected state
    const [selectedPlanType, setSelectedPlanType] = useState<string>('');
    const [selectedUsersTier, setSelectedUsersTier] = useState<SubscriptionAddonTier | null>(null);
    const [selectedSmsTier, setSelectedSmsTier] = useState<SubscriptionAddonTier | null>(null);
    const [selectedWhatsAppTier, setSelectedWhatsAppTier] = useState<SubscriptionAddonTier | null>(null);
    const [selectedFeatureAddons, setSelectedFeatureAddons] = useState<Record<string, SubscriptionAddonTier | boolean>>({});
    const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly');
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const hiddenEmailAllowance = 3000;
    const includedStorage = 5;

    const planTaglines = useMemo(() => ({
        basic: t(TK.subscriptionPlanBasicTagline),
        pro: t(TK.subscriptionPlanProTagline),
        proplus: t(TK.subscriptionPlanProPlusTagline),
    }), [t]);

    const applyPlanTaglines = useCallback((data: SubscriptionDataResponse) => {
        const plansWithTaglines = Object.fromEntries(
            Object.entries(data.plans).map(([key, plan]) => {
                const normalizedKey = (plan.type || key || '').toString().toLowerCase();
                const tagline = planTaglines[normalizedKey as keyof typeof planTaglines];
                return [key, {
                    ...plan,
                    description: tagline ?? plan.description,
                }];
            })
        );

        return { ...data, plans: plansWithTaglines };
    }, [planTaglines]);

    const getOrderedPlanKeys = useCallback((plans: Record<string, SubscriptionPlanDefinition>) => {
        // Sort plans by their displayOrder field (if available), otherwise use alphabetical order
        const availablePlanKeys = Object.keys(plans);
        return availablePlanKeys.sort((a, b) => {
            const planA = plans[a];
            const planB = plans[b];

            // If plans have displayOrder, use it; otherwise fallback to name comparison
            if (planA.displayOrder !== undefined && planB.displayOrder !== undefined) {
                return planA.displayOrder - planB.displayOrder;
            }
            return (planA.name || a).localeCompare(planB.name || b);
        });
    }, []);

    useEffect(() => {
        const loadSubscriptionData = async () => {
            try {
                const [data, stats] = await Promise.all([
                    getSubscriptionsAsync(),
                    getAdminStatsAsync().catch(() => null),
                ]);
                const enhancedData = applyPlanTaglines(data);
                const current = enhancedData.currentSubscription;
                // Prefer nextSubscription if available, otherwise use currentSubscription
                const subscriptionToLoad = enhancedData.nextSubscription ?? current;

                const orderedPlans = getOrderedPlanKeys(enhancedData.plans);
                const defaultPlanKey = orderedPlans[0] ?? '';

                // Determine initial plan selection - prefer nextSubscription, then current subscription, fallback to first available plan
                let initialPlan: string = subscriptionToLoad?.subscriptionName || defaultPlanKey;

                const plan = enhancedData.plans[initialPlan] || enhancedData.plans[initialPlan.toLowerCase()];
                const normalizedPlanKey = plan
                    ? (Object.keys(enhancedData.plans).find(key => key.toLowerCase() === (plan.type || initialPlan).toLowerCase()) ||
                        initialPlan ||
                        defaultPlanKey)
                    : defaultPlanKey || initialPlan;

                setSelectedPlanType(normalizedPlanKey);

                // Helper function to resolve addons
                const getAddon = (key: string) =>
                    enhancedData.addons[key] || enhancedData.addons[key.toLowerCase()] || enhancedData.addons[key.toUpperCase()];

                // Auto-populate from subscription (nextSubscription if available, otherwise currentSubscription)
                if (subscriptionToLoad) {
                    // Find and select Users tier based on UsersCount
                    const usersAddon = getAddon('Users');
                    const planIncludedUsers = plan?.includedUsers ?? 0;
                    if (usersAddon?.tiers && subscriptionToLoad.usersCount > planIncludedUsers) {
                        const usersTier = usersAddon.tiers.find(tier => tier.quantity === subscriptionToLoad.usersCount - planIncludedUsers);
                        if (usersTier) setSelectedUsersTier(usersTier);
                    }

                    // Find and select SMS tier based on SmsAllowed
                    const smsAddon = getAddon('Sms');
                    if (smsAddon?.tiers && subscriptionToLoad.smsAllowed > 0) {
                        const smsTier = smsAddon.tiers.find(tier => tier.quantity === subscriptionToLoad.smsAllowed);
                        if (smsTier) setSelectedSmsTier(smsTier);
                    }

                    // Find and select WhatsApp tier based on WhatsAppAllowed
                    const whatsAppAddon = getAddon('WhatsApp');
                    if (whatsAppAddon?.tiers && subscriptionToLoad.whatsAppAllowed > 0) {
                        const whatsAppTier = whatsAppAddon.tiers.find(tier => tier.quantity === subscriptionToLoad.whatsAppAllowed);
                        if (whatsAppTier) setSelectedWhatsAppTier(whatsAppTier);
                    }

                    // Pre-select feature addons
                    const features: Record<string, boolean> = {};
                    if (!isFrenchCountry && subscriptionToLoad.hasInvoices) features['billing'] = true;
                    if (subscriptionToLoad.hasMintuChatbot) features['mintu'] = true;
                    if (subscriptionToLoad.hasIntegrations) features['integrations'] = true;
                    setSelectedFeatureAddons(features);
                }

                setSubscriptionData(enhancedData);
                setAdminStats(stats);
            } catch (error) {
                console.error('Failed to load subscription data:', error);
                setLoading(false);
            } finally {
                setLoading(false);
            }
        };

        loadSubscriptionData();
    }, [applyPlanTaglines, getOrderedPlanKeys, isFrenchCountry]);

    useEffect(() => {
        if (!isFrenchCountry) {
            return;
        }

        setSelectedFeatureAddons(prev => {
            const next = { ...prev };
            delete next.billing;
            delete next.Billing;
            return next;
        });
    }, [isFrenchCountry]);

    // Helper to resolve plan by case-insensitive lookup
    const resolvePlan = useCallback((planType: string) => {
        if (!subscriptionData) return null;
        return subscriptionData.plans[planType] || subscriptionData.plans[planType.toLowerCase()];
    }, [subscriptionData]);

    // Helper to resolve addon by case-insensitive lookup
    const resolveAddon = useCallback((key: string): SubscriptionAddonDefinition | undefined => {
        if (!subscriptionData) return undefined;
        return subscriptionData.addons[key] || subscriptionData.addons[key.toLowerCase()] || subscriptionData.addons[key.toUpperCase()];
    }, [subscriptionData]);

    const selectedPlan = useMemo(() => resolvePlan(selectedPlanType), [resolvePlan, selectedPlanType]);

    const includedUsers = useMemo(() => {
        return selectedPlan?.includedUsers || 0;
    }, [selectedPlan]);

    const allFeatures = useMemo(() => {
        if (!subscriptionData) return [];

        const featureMap = new Map<string, SubscriptionFeatureDefinition>();
        Object.keys(subscriptionData.plans).forEach((planKey) => {
            const plan = resolvePlan(planKey);
            (plan?.features || []).forEach((feature) => {
                if (!featureMap.has(feature.key)) {
                    featureMap.set(feature.key, feature);
                }
            });
        });

        return Array.from(featureMap.values());
    }, [subscriptionData, resolvePlan]);

    const planOrder = useMemo(() => {
        if (!subscriptionData) return [];
        return getOrderedPlanKeys(subscriptionData.plans);
    }, [getOrderedPlanKeys, subscriptionData]);

    const handlePlanChange = (planType: string) => {
        setSelectedPlanType(planType);
        // Reset users tier when changing plans (back to included amount)
        setSelectedUsersTier(null);
    };

    const handleAddonChange = (addonType: string, value: SubscriptionAddonTier | boolean | null) => {
        if (value === null) {
            const { [addonType]: _, ...rest } = selectedFeatureAddons;
            setSelectedFeatureAddons(rest);
        } else {
            setSelectedFeatureAddons({
                ...selectedFeatureAddons,
                [addonType]: value,
            });
        }
    };

    const disabledFeatureAddons = useMemo(() => {
        if (!isFrenchCountry || !subscriptionData?.addons) {
            return {};
        }

        const result: Record<string, string> = {};
        Object.entries(subscriptionData.addons).forEach(([addonKey, addon]) => {
            const normalizedAddonKey = addonKey.toLowerCase();
            const addonDefinitionType = (addon.type ?? '').toLowerCase();
            const addonName = (addon.name ?? '').toLowerCase();
            if (normalizedAddonKey === 'billing' || addonDefinitionType === 'billing' || addonName === 'billing') {
                result[addonKey] = t(TK.invoicesNotSupportedInThisCountry);
            }
        });

        return result;
    }, [isFrenchCountry, subscriptionData?.addons, t]);

    // Calculate current monthly total based on current subscription
    const currentMonthlyTotal = useMemo(() => {
        if (!subscriptionData?.currentSubscription) return undefined;

        const current = subscriptionData.currentSubscription;
        const currentPlan = resolvePlan(current.subscriptionName || selectedPlanType);
        if (!currentPlan) return undefined;

        const resolvePlanBasePrice = (plan: SubscriptionPlanDefinition) =>
            billingPeriod === 'yearly'
                ? plan.pricePerMonthBilledAnnually ?? plan.pricePerMonthBilledMonthly ?? 0
                : plan.pricePerMonthBilledMonthly ?? plan.pricePerMonthBilledAnnually ?? 0;

        let total = resolvePlanBasePrice(currentPlan);

        // Add Users tier cost (only if above plan included)
        const usersAddon = resolveAddon('Users');
        if (usersAddon?.tiers && current.usersCount > (currentPlan?.includedUsers || 0)) {
            const tier = usersAddon.tiers.find(t => t.quantity === current.usersCount - currentPlan.includedUsers);
            if (tier) total += tier.pricePerMonth;
        }

        // Add SMS tier cost
        const smsAddon = resolveAddon('Sms');
        if (smsAddon?.tiers && current.smsAllowed > 0) {
            const tier = smsAddon.tiers.find(t => t.quantity === current.smsAllowed);
            if (tier) total += tier.pricePerMonth;
        }

        // Add WhatsApp tier cost
        const whatsAppAddon = resolveAddon('WhatsApp');
        if (whatsAppAddon?.tiers && current.whatsAppAllowed > 0) {
            const tier = whatsAppAddon.tiers.find(t => t.quantity === current.whatsAppAllowed);
            if (tier) total += tier.pricePerMonth;
        }

        // Add feature addons
        const billingAddon = resolveAddon('Billing');
        if (current.hasInvoices && billingAddon?.tiers?.length === 1) {
            total += getAddonTierPrice(billingAddon.tiers[0], billingPeriod);
        }

        const mintuAddon = resolveAddon('Mintu');
        if (current.hasMintuChatbot && mintuAddon?.tiers?.length === 1) {
            total += mintuAddon.tiers[0].pricePerMonth;
        }

        const integrationsAddon = resolveAddon('Integrations');
        if (current.hasIntegrations && integrationsAddon?.tiers?.length === 1) {
            total += integrationsAddon.tiers[0].pricePerMonth;
        }

        // Apply discounts
        if (subscriptionData.discounts && subscriptionData.discounts.length > 0) {
            const totalDiscount = subscriptionData.discounts.reduce((sum, discount) => {
                const discountValue = billingPeriod === 'yearly'
                    ? discount.yearlyDiscountValue
                    : discount.monthlyDiscountValue;
                return sum + discountValue;
            }, 0);
            total -= totalDiscount;
        }

        return Math.max(0, total);
    }, [billingPeriod, resolveAddon, resolvePlan, selectedPlanType, subscriptionData]);

    const handleUpgradeSubscription = async () => {
        try {
            setIsUpgrading(true);

            const subscriptionData: SubscriptionData = {
                subscriptionName: selectedPlanType,
                usersCount: includedUsers + (selectedUsersTier?.quantity ?? 0),
                smsAllowed: selectedSmsTier?.quantity ?? 0,
                whatsAppAllowed: selectedWhatsAppTier?.quantity ?? 0,
                emailAllowed: hiddenEmailAllowance,
                storageAllowed: includedStorage,
                hasInvoices: !isFrenchCountry && !!selectedFeatureAddons['billing'],
                hasMintuChatbot: !!selectedFeatureAddons['mintu'],
                hasIntegrations: !!selectedFeatureAddons['integrations'],
                billedAnnually: billingPeriod === 'yearly',
                isTrial: false,
            };

            await upgradeSubscriptionAsync(subscriptionData);
            dispatch(alertSuccess(t(TK.subscriptionUpgraded)));

            // Reload subscription data
            const [data, stats] = await Promise.all([
                getSubscriptionsAsync(),
                getAdminStatsAsync().catch(() => null),
            ]);
            const enhancedData = applyPlanTaglines(data);
            setSubscriptionData(enhancedData);
            setAdminStats(stats);
            setIsHistoryModalOpen(true);
        } catch (error) {
            console.error('Failed to upgrade subscription:', error);
            dispatch(alertError(t(TK.failedToUpgradeSubscription)));
        } finally {
            setIsUpgrading(false);
        }
    };

    const supportMessage = t(TK.subscriptionChangesSupportMessage);

    if (loading) {
        return (
            <TitledContainer title={t(TK.mySubscription)}>
                <div style={{ padding: 'var(--spacer-M)' }}>{t(TK.loading)}</div>
            </TitledContainer>
        );
    }

    if (!subscriptionData || !selectedPlan) {
        return (
            <TitledContainer title={t(TK.mySubscription)}>
                <div style={{ padding: 'var(--spacer-M)' }}>
                    {t(TK.failedToLoadSubscriptionData)}. {t(TK.pleaseRetryLater)}.
                </div>
            </TitledContainer>
        );
    }

    return (
        <SubscriptionContainer>
            {paymentExpired && (
                <div style={{
                    padding: 'var(--spacer-S)',
                    marginBottom: 'var(--spacer-M)',
                    backgroundColor: 'var(--warning-bg)',
                    border: 'var(--border-thin) solid var(--warning-border)',
                    borderLeft: 'var(--border-thick) solid var(--warning-color)',
                    borderRadius: '4px',
                    color: 'var(--text-body)',
                }}>
                    <strong>{t(TK.subscriptionExpired)}</strong>
                </div>
            )}
            {/* Subscription Overview */}
            <SubscriptionOverview
                currentSubscription={subscriptionData.currentSubscription}
                nextSubscription={subscriptionData.nextSubscription}
                currentMonthlyTotal={currentMonthlyTotal}
                usageStats={adminStats}
                isHistoryModalOpen={isHistoryModalOpen}
                onHistoryModalOpen={() => setIsHistoryModalOpen(true)}
                onHistoryModalClose={() => setIsHistoryModalOpen(false)}
            />

            <div role="group" aria-label="Billing period" style={{alignSelf: 'center'}}>
                <span style={{fontSize: '0.8rem'}}>{t(TK.payment)}: </span>
                <ToggleButtonGroup
                    value={billingPeriod}
                    exclusive
                    onChange={(_, value) => {
                        if (value) {
                            setBillingPeriod(value)
                            if (value === 'monthly') {
                                subscriptionData.addons && Object.entries(subscriptionData.addons).forEach(([type, addon]) => {
                                    if (!addon.availableForMontlhyBilling) {
                                        // Reset addons that are not available for monthly billing
                                        handleAddonChange(type, null);
                                    }
                                });
                            }
                        }
                    }}
                    size="small"
                    sx={{
                        backgroundColor: '#e8e8e8',
                        borderRadius: '18px',
                        padding: '3px',
                        gap: 0,
                        '& .MuiToggleButton-root': {
                            border: 'none',
                            borderRadius: '15px !important',
                            px: 1.8,
                            py: 0.5,
                            minWidth: '70px',
                            textTransform: 'lowercase',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: '#666',
                            backgroundColor: 'transparent',
                            transition: 'all 0.2s ease-out',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                            },
                            '&.Mui-selected': {
                                backgroundColor: '#10b981',
                                color: '#fff',
                                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.35)',
                                '&:hover': {
                                    backgroundColor: '#10b981',
                                },
                            },
                        },
                    }}
                >
                    <ToggleButton value="monthly">
                        {t(TK.monthly)}
                    </ToggleButton>
                    <ToggleButton value="yearly">
                        {t(TK.yearly)}
                    </ToggleButton>
                </ToggleButtonGroup>
            </div>
            
            {/* Step 1: Choose Plan */}
            <div className="step-section">
                <PlanComparison
                    plans={subscriptionData.plans}
                    features={allFeatures}
                    selectedPlan={selectedPlanType}
                    onSelectPlan={handlePlanChange}
                    billingPeriod={billingPeriod}
                    planOrder={planOrder}
                    currentPlan={subscriptionData.currentSubscription?.subscriptionName || null}
                    lastPaymentDate={subscriptionData.currentSubscription?.lastPaymentDate || null}
                    paymentValidityDate={subscriptionData.currentSubscription?.paymentValidityDate || null}
                />
            </div>

            {/* Step 2: Configure Extras */}
            <div className="step-section">
                {/* Usage Sliders */}
                <UsageSliders
                    includedUsers={includedUsers}
                    addons={subscriptionData.addons}
                    onUsersChange={setSelectedUsersTier}
                    onSmsChange={setSelectedSmsTier}
                    onWhatsAppChange={setSelectedWhatsAppTier}
                    selectedUsersTier={selectedUsersTier}
                    selectedSmsTier={selectedSmsTier}
                    selectedWhatsAppTier={selectedWhatsAppTier}
                    currentSmsAllowed={subscriptionData.currentSubscription?.smsAllowed}
                    currentWhatsAppAllowed={subscriptionData.currentSubscription?.whatsAppAllowed}
                    currentUsersCount={subscriptionData.currentSubscription?.usersCount}
                />

                {/* Feature Add-ons */}
                <FeatureAddons
                    billingPeriod={billingPeriod}
                    addons={subscriptionData.addons}
                    selectedAddons={selectedFeatureAddons}
                    onAddonChange={handleAddonChange}
                    disabledAddons={disabledFeatureAddons}
                />

                {/* Price Calculator */}
                <PriceCalculator
                    selectedPlan={selectedPlan}
                    selectedUsersTier={selectedUsersTier}
                    selectedSmsTier={selectedSmsTier}
                    selectedWhatsAppTier={selectedWhatsAppTier}
                    selectedFeatureAddons={selectedFeatureAddons}
                    addons={subscriptionData.addons}
                    billingPeriod={billingPeriod}
                    currentMonthlyTotal={currentMonthlyTotal}
                    discounts={subscriptionData.discounts}
                    onHasChangesChange={setHasChanges}
                    currentPlan={subscriptionData.currentSubscription && !subscriptionData.currentSubscription.isTrial ? resolvePlan(subscriptionData.currentSubscription.subscriptionName || '') ?? undefined : undefined}
                    currentUsersTier={
                        subscriptionData.currentSubscription
                            ? (() => {
                                const currentPlan = resolvePlan(subscriptionData.currentSubscription!.subscriptionName || '');
                                const included = currentPlan?.includedUsers || 0;
                                if (subscriptionData.currentSubscription!.usersCount > included) {
                                    return resolveAddon('Users')?.tiers?.find(
                                        tier => tier.quantity === subscriptionData.currentSubscription!.usersCount - included
                                    ) ?? null;
                                }
                                return null;
                            })()
                            : null
                    }
                    currentSmsTier={
                        subscriptionData.currentSubscription?.smsAllowed
                            ? resolveAddon('Sms')?.tiers?.find(
                                tier => tier.quantity === subscriptionData.currentSubscription!.smsAllowed
                              )
                            : null
                    }
                    currentWhatsAppTier={
                        subscriptionData.currentSubscription?.whatsAppAllowed
                            ? resolveAddon('WhatsApp')?.tiers?.find(
                                tier => tier.quantity === subscriptionData.currentSubscription!.whatsAppAllowed
                              )
                            : null
                    }
                    currentFeatureAddons={
                        subscriptionData.currentSubscription ? {
                            billing: !isFrenchCountry && (subscriptionData.currentSubscription.hasInvoices ?? false),
                            mintu: subscriptionData.currentSubscription.hasMintuChatbot ?? false,
                            integrations: subscriptionData.currentSubscription.hasIntegrations ?? false,
                        } : undefined
                    }
                />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--spacer-M)', marginTop: 'var(--spacer-L)', marginBottom: 'var(--spacer-L)' }}>
                <Tooltip title={supportMessage} arrow slotProps={{ tooltip: { style: { fontSize: '14px', padding: '8px 12px' } } }}>
                    <span>
                        <Button variant="outlined" color="secondary" disabled>
                            {t(TK.cancelSubscription)}
                        </Button>
                    </span>
                </Tooltip>
                
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpgradeSubscription}
                    disabled={isUpgrading}
                >
                    {isUpgrading ? t(TK.loading) : t(TK.upgradeSubscription)}
                </Button>
            </div>
        </SubscriptionContainer>
    );
};

export default MySubscription;
